// Pad-driven imagery loading for restricted operators.
//
// Problem: a restricted operator's case can span pads hundreds of miles
// apart (dispersed "UL-style" cases). Showing AOI-scoped imagery while the
// camera sits at the operating-AOI overview (z=6-9) makes MapLibre request
// low-zoom cutline-masked tiles. Masking a handful of tiny pad polygons
// against millions of pixels per tile is slow enough to 502/504 on cold
// start (confirmed via curl + browser).
//
// Fix: imagery is only visible while the operator is inspecting a specific
// pad. QcInspector auto-zooms to the current pad (~z17), so imagery only
// ever loads for that small, high-zoom area — cheap cutline, fast tiles.
// No current pad → imagery hidden → zero low-zoom tile fetches.
//
// WHY toggle the stack `visible` flag (not setLayoutProperty): imagery is a
// dynamic stack (useRestrictedAutoEnableImagery registers every entitled
// series). useSyncRegistryLayers gates BOTH render and tile-fetching on each
// registry layer's `visible` flag — syncImageryLayer early-returns (no
// /tile/session, no tile URL) when a layer is not visible. So flipping the
// stack `visible` flag stops the flood at the source, and the registry sync
// stays the single applier. A manual setLayoutProperty would only suppress
// pixels (tiles still fetched) and would be fought by the registry sync.
//
// Division of labor:
//   - useRestrictedAutoEnableImagery REGISTERS the entitled imagery layers
//     (added visible:false for restricted operators — see the note there).
//   - THIS hook is the sole authority on imagery VISIBILITY, driven by pad
//     context.
//   - QcInspector owns the camera (fitBounds to the current pad).
//
// Non-restricted users (DH/customer analysts): no-op. They keep imagery-
// always-on for legitimate cross-pad browsing, controlled via LayerPanel.

import { useEffect } from "react";

import { useAuthStore, selectIsRestrictedOperator } from "../stores/useAuthStore";
import { useQcStore } from "../stores/useQcStore";
import { useImageryStackStore } from "../stores/useImageryStackStore";

export function useRestrictedPadDrivenImagery() {
  const isRestricted = useAuthStore(selectIsRestrictedOperator);
  const session = useQcStore((s) => s.session);
  const currentPadIdx = useQcStore((s) => s.currentPadIdx);
  const stack = useImageryStackStore((s) => s.stack);
  const setLayerVisible = useImageryStackStore((s) => s.setLayerVisible);

  // A pad is "active" when the operator is inspecting one. currentPadIdx is
  // an index into session.pads (0 is a valid index, so test against null),
  // and the pad must actually exist in the loaded session.
  const padActive = currentPadIdx != null && !!session?.pads?.[currentPadIdx];

  useEffect(() => {
    // Non-restricted users manage imagery through LayerPanel — never touch
    // their visibility from here.
    if (!isRestricted) return;

    // Sole-authority sync: every imagery stack layer follows pad context.
    // Only flip entries that actually differ, so the effect no-ops once
    // converged (the resulting stack change re-runs this effect, but with
    // every entry already correct it does nothing — no reconcile loop).
    for (const entry of stack) {
      if (entry.visible !== padActive) {
        setLayerVisible(entry.layer_id, padActive);
      }
    }

    // CONTINGENCY (scenarios 6 & 7): the visible:true flip currently fires
    // synchronously with QcInspector's fitBounds. If the camera animation
    // starts at low zoom, MapLibre may request intermediate low-zoom cutline
    // tiles mid-flight. MapLibre cancels off-screen requests, but a cancelled
    // request can still have triggered the server-side mask. If network-panel
    // verification shows tile fetches at z<13 during the fitBounds animation,
    // gate the visible:true flip on the map settling: grab
    // useAppViewStore.mapInstance, and when padActive becomes true do
    //   map.isMoving() ? map.once("moveend", reveal) : reveal()
    // so imagery only mounts once the camera reaches the pad's high zoom.
    // (Hiding on pad→null should stay synchronous — stopping fetches early
    // is always safe.)

    /**
     * On pad→null transition, we hide imagery but do NOT fitBounds to
     * operating AOI overview. The no-pad state is transient — it only
     * occurs during case unload (operator navigating away). Adding
     * overview fitBounds would compete with QcInspector's pad-fit on the
     * next case open. If a future workflow introduces an observable
     * no-pad state (e.g., case overview tab), revisit here.
     */
  }, [isRestricted, padActive, stack, setLayerVisible]);
}
